/**
 * Multiple video providers: same behavior as YouTube (normalize, strip, embed).
 * Supported: YouTube, Vimeo, TikTok, Dailymotion.
 */

// ─── YouTube ─────────────────────────────────────────────────────────────
const YOUTUBE_FULL =
  /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?[^\s]*|youtu\.be\/[^\s]*|youtube\.com\/(?:embed\/|v\/)[^\s]*)/gi;
function extractYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/|youtube\.com\/v\/)([^&\n?#]+)/,
    /youtube\.com\/watch\?.*v=([^&\n?#]+)/,
  ];
  for (const p of patterns) {
    const m = trimmed.match(p);
    if (m && m[1]) return m[1];
  }
  return null;
}

// ─── Vimeo ───────────────────────────────────────────────────────────────
const VIMEO_FULL =
  /(?:https?:\/\/)?(?:www\.)?(?:player\.)?vimeo\.com\/(?:video\/)?[^\s]*/gi;
function extractVimeoId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const m = trimmed.match(/(?:vimeo\.com\/|player\.vimeo\.com\/video\/)(\d+)/);
  return m && m[1] ? m[1] : null;
}

// ─── TikTok ──────────────────────────────────────────────────────────────
const TIKTOK_FULL =
  /(?:https?:\/\/)?(?:www\.)?(?:vm\.)?tiktok\.com\/[^\s]*/gi;
function extractTikTokId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const m = trimmed.match(/tiktok\.com\/(?:@[^/]+\/video\/|embed\/video\/|video\/)(\d+)/);
  return m && m[1] ? m[1] : null;
}

// ─── Dailymotion ──────────────────────────────────────────────────────────
const DAILYMOTION_FULL =
  /(?:https?:\/\/)?(?:www\.)?dailymotion\.com\/[^\s]*/gi;
function extractDailymotionId(url) {
  if (!url || typeof url !== 'string') return null;
  const trimmed = url.trim();
  const m = trimmed.match(/dailymotion\.com\/(?:video\/|embed\/video\/)([a-zA-Z0-9]+)/);
  return m && m[1] ? m[1] : null;
}

// ─── Provider config (order: first match in text wins) ───────────────────
const PROVIDERS = [
  {
    id: 'youtube',
    name: 'YouTube',
    fullRegex: YOUTUBE_FULL,
    extractId: extractYouTubeId,
    cleanUrl: (id) => `https://www.youtube.com/watch?v=${id}`,
    getEmbedUrl: (id, _opts) => `https://www.youtube.com/embed/${id}`,
    getThumbnailUrl: (id) => `https://img.youtube.com/vi/${id}/hqdefault.jpg`,
    playButtonClass: 'bg-red-600',
  },
  {
    id: 'vimeo',
    name: 'Vimeo',
    fullRegex: VIMEO_FULL,
    extractId: extractVimeoId,
    cleanUrl: (id) => `https://vimeo.com/${id}`,
    getEmbedUrl: (id) => `https://player.vimeo.com/video/${id}`,
    getThumbnailUrl: (id) => `https://vumbnail.com/${id}.jpg`,
    playButtonClass: 'bg-blue-600',
  },
  {
    id: 'tiktok',
    name: 'TikTok',
    fullRegex: TIKTOK_FULL,
    extractId: extractTikTokId,
    cleanUrl: (id) => `https://www.tiktok.com/video/${id}`,
    getEmbedUrl: (id) => `https://www.tiktok.com/embed/video/${id}`,
    getThumbnailUrl: () => null,
    playButtonClass: 'bg-gray-900',
  },
  {
    id: 'dailymotion',
    name: 'Dailymotion',
    fullRegex: DAILYMOTION_FULL,
    extractId: extractDailymotionId,
    cleanUrl: (id) => `https://www.dailymotion.com/video/${id}`,
    getEmbedUrl: (id) => `https://www.dailymotion.com/embed/video/${id}`,
    getThumbnailUrl: (id) => `https://www.dailymotion.com/thumbnail/video/${id}`,
    playButtonClass: 'bg-orange-500',
  },
];

/** All regexes combined to match any supported video URL (full URL to next space/end) */
const ALL_VIDEO_URL_REGEX = new RegExp(
  PROVIDERS.map((p) => p.fullRegex.source).join('|'),
  'gi'
);

/**
 * Find the first video URL in text (any provider). Returns { provider, videoId } or null.
 */
export function findFirstVideo(text) {
  if (!text || typeof text !== 'string') return null;
  let earliest = { index: Infinity, provider: null, videoId: null };
  for (const provider of PROVIDERS) {
    const re = new RegExp(provider.fullRegex.source, 'gi');
    let m;
    while ((m = re.exec(text)) !== null) {
      const id = provider.extractId(m[0]);
      if (id && m.index < earliest.index) {
        earliest = { index: m.index, provider, videoId: id };
      }
    }
  }
  return earliest.provider ? { provider: earliest.provider, videoId: earliest.videoId } : null;
}

/**
 * Normalize all supported video URLs in text to clean watch URLs (no extra refs).
 */
export function normalizeVideoUrlsInText(text) {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  for (const provider of PROVIDERS) {
    const re = new RegExp(provider.fullRegex.source, 'gi');
    out = out.replace(re, (fullUrl) => {
      const id = provider.extractId(fullUrl);
      return id ? provider.cleanUrl(id) : fullUrl;
    });
  }
  return out;
}

/**
 * Strip all supported video URLs from text (for display when embed is shown).
 */
export function stripVideoUrlsFromText(text) {
  if (!text || typeof text !== 'string') return text;
  let out = text;
  for (const provider of PROVIDERS) {
    const re = new RegExp(provider.fullRegex.source, 'gi');
    out = out.replace(re, '');
  }
  return out.replace(/\n\s*\n+/g, '\n').trim();
}

export function getProvider(id) {
  return PROVIDERS.find((p) => p.id === id) || null;
}

export { PROVIDERS };
